import type { TextInputContainerProps } from './TextInputContainer.tsx';
import { useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { useRef } from 'react';
import { TextInputClickThrough } from './TextInputClickThrough.tsx';
import { TextInputContainer } from './TextInputContainer.tsx';
import { useTextInputGroup } from './useTextInputGroup.ts';

export interface TextInputProps
  extends
    Pick<
      React.ComponentPropsWithRef<'input'>,
      | 'autoFocus'
      | 'autoComplete'
      | 'disabled'
      | 'id'
      | 'name'
      | 'onBlur'
      | 'onChange'
      | 'onFocus'
      | 'onInput'
      | 'maxLength'
      | 'placeholder'
      | 'ref'
      | 'required'
      | 'type'
      | 'value'
    >,
    Pick<TextInputContainerProps, 'cornerText' | 'error' | 'helpText' | 'label' | 'rounded'> {
  align?: 'left' | 'center' | 'right';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

export type TextInputRef = HTMLInputElement;

export function TextInput(props: TextInputProps) {
  const {
    align = 'left',
    autoFocus,
    cornerText,
    disabled,
    error,
    helpText,
    label,
    name,
    placeholder,
    prefix,
    required,
    rounded,
    suffix,
    type = 'text',
    value,
    ref: externalRef,
    ...otherProps
  } = props;

  const internalInputRef = useRef<TextInputRef>(null);
  const ref = useMergedRef<TextInputRef>(externalRef, internalInputRef);

  const inputGroup = useTextInputGroup();

  const contentMarkup = (
    <div className="flex h-full w-full items-stretch">
      {prefix && (
        <TextInputClickThrough inputRef={internalInputRef}>
          <div
            className={clsx(
              'pr-1.5 pl-3 text-base whitespace-nowrap sm:text-sm',
              error && 'text-red-500',
              disabled && 'text-gray-300',
              !disabled && !error && 'text-gray-400'
            )}
          >
            {prefix}
          </div>
        </TextInputClickThrough>
      )}
      <input
        {...otherProps}
        autoFocus={autoFocus}
        className={clsx(
          'block w-full grow resize-none appearance-none border-0 bg-transparent py-[calc(--spacing(1.5)-1px)] leading-6 focus-within:outline-hidden focus:ring-0 sm:text-sm sm:leading-6',
          prefix ? 'pl-0.5' : 'pl-3',
          suffix ? 'pr-0.5' : 'pr-3',
          disabled && 'text-gray-400 placeholder-gray-300',
          error && 'placeholder-red-300',
          !disabled && !error && 'placeholder-gray-400',
          align === 'left' && 'text-left',
          align === 'center' && 'text-center',
          align === 'right' && 'text-right'
        )}
        data-autofocus={autoFocus}
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        ref={ref}
        required={required}
        type={type}
        value={value}
      />
      {suffix && (
        <TextInputClickThrough inputRef={internalInputRef}>
          <div
            className={clsx(
              'pr-3 pl-1.5 text-base whitespace-nowrap sm:text-sm',
              error && 'text-red-500',
              disabled && 'text-gray-300',
              !disabled && !error && 'text-gray-400'
            )}
          >
            {suffix}
          </div>
        </TextInputClickThrough>
      )}
    </div>
  );

  return inputGroup ? (
    contentMarkup
  ) : (
    <TextInputContainer
      cornerText={cornerText}
      disabled={disabled}
      error={error}
      label={label}
      helpText={helpText}
      rounded={rounded}
    >
      {contentMarkup}
    </TextInputContainer>
  );
}
